// Notes SC6a R3a — the clinical note's rich field: a Tiptap editor wrapped in the TEXTAREA CONTRACT the autosave engine
// relies on (investigation 2026-09-27 §3), so the engine keeps working unchanged:
//   .value      the plain-text mirror (docToPlain) — what `content` stores; setting it replaces the doc (one undo step)
//   .doc        the Tiptap JSON (content_doc) — never put into DB.vaultNotes / localStorage (PHI scrubber rule)
//   .disabled   true = content not loaded yet (the engine holds saves) — the editor is not editable
//   .readOnly   locked note / deleted elsewhere
//   .oninput / addEventListener('input') / dispatchEvent('input')   local edits only (never a remote apply)
//   destroy()   keeps the last doc + mirror, so a background save of a released engine (A4) still reads the full text
// One instance per open note survives re-renders of that note (editor.js keeps it); the host element is simply moved into
// the new pane, so the undo history is never lost to a re-render.
import { plainToDoc, docToPlain, emptyDoc } from './rich-doc.js';
import { loadNotesRich } from './rich-loader.js';

// Decision 25.5 — the old-client rule: a doc whose mirror no longer equals `content` is stale (a pre-SC6 client wrote
// `content` only) → `content` wins, converted. → { doc, from: 'doc' | 'plain' | 'stale' }
export function docForRow(content, contentDoc){
  const c = content || '';
  if(contentDoc && typeof contentDoc === 'object' && contentDoc.type === 'doc' && Array.isArray(contentDoc.content)){
    if(docToPlain(contentDoc) === c) return { doc: contentDoc, from: 'doc' };
    return { doc: plainToDoc(c), from: 'stale' };
  }
  return { doc: plainToDoc(c), from: 'plain' };
}

export class RichField {
  constructor({ noteId, placeholder = '', readOnly = false, onForeignTrip = null } = {}){
    this.noteId = noteId;
    this._onForeignTrip = onForeignTrip; // F-59b-2: the freeze watchdog's device copy (editor.js)
    this.isRich = true;
    this.el = document.createElement('div');
    this.el.className = 'vn2-rich-host';
    this.el.setAttribute('data-note-rich', noteId);
    this.el._hudField = this; // test hook: the harnesses reach the field through its host element
    this.editor = null;
    this._doc = emptyDoc(); this._plain = '';
    this._disabled = true; this._readOnly = !!readOnly; this._placeholder = placeholder;
    this.loaded = false; this.destroyed = false; this.mountError = null;
    this.oninput = null; this._inputL = new Set(); this._silent = 0;
    this.onStateChange = null; this._onFocusChange = null; this._pinDispose = null; // SC6a R4: the toolbar's repaint + the phone pin
    this._scroller = null;
  }
  // ── the textarea contract ──
  get value(){ return this.editor ? docToPlain(this.editor.getJSON()) : this._plain; }
  set value(v){ this.setDoc(plainToDoc(v == null ? '' : String(v)), { undoable: true }); } // legacy writers (plain text)
  get doc(){ return this.editor ? this.editor.getJSON() : this._doc; }
  get disabled(){ return this._disabled; }
  set disabled(v){ this._disabled = !!v; this._applyEditable(); }
  get readOnly(){ return this._readOnly; }
  set readOnly(v){ this._readOnly = !!v; this._applyEditable(); }
  get placeholder(){ return this.el.getAttribute('data-loading') || ''; }
  set placeholder(v){ if(v) this.el.setAttribute('data-loading', v); else this.el.removeAttribute('data-loading'); }
  get dataset(){ return this.el.dataset; }
  get scrollTop(){ return this._scroller ? this._scroller.scrollTop : 0; }
  set scrollTop(v){ if(this._scroller) this._scroller.scrollTop = v; }
  focus(){ if(this.editor && !this.destroyed) this.editor.commands.focus(); }
  // Caret as a PLAIN-TEXT offset (textarea parity): the text before the caret with '\n' between blocks.
  get selectionStart(){ return this.editor ? this.editor.state.doc.textBetween(0, this.editor.state.selection.from, '\n').length : null; }
  get selectionEnd(){ return this.editor ? this.editor.state.doc.textBetween(0, this.editor.state.selection.to, '\n').length : null; }
  setSelectionRange(a, b){
    if(!this.editor) return;
    const doc = this.editor.state.doc, size = doc.content.size;
    const at = (off) => { let lo = 0, hi = size; while(lo < hi){ const mid = (lo + hi) >> 1; if(doc.textBetween(0, mid, '\n').length < off) lo = mid + 1; else hi = mid; } return Math.min(Math.max(lo, 1), size); };
    this.editor.commands.setTextSelection({ from: at(a), to: at(b == null ? a : b) });
  }
  addEventListener(t, fn){ if(t === 'input') this._inputL.add(fn); }
  removeEventListener(t, fn){ if(t === 'input') this._inputL.delete(fn); }
  dispatchEvent(ev){ if(ev && ev.type === 'input') this._fireInput(); return true; }
  _fireInput(){ try{ if(this.oninput) this.oninput(); }catch(e){ console.error('❌ rich field input:', e); } for(const f of [...this._inputL]){ try{ f(); }catch(e){ console.error('❌ rich field input listener:', e); } } }
  _applyEditable(){
    const paused = !!(this.editor && this.editor.view && this.editor.view.foreignDomGuard && this.editor.view.foreignDomGuard.paused); // F-59b-2: the freeze watchdog paused it; only its Continue button resumes
    const on = !this._disabled && !this._readOnly && !paused;
    this.el.classList.toggle('vn2-rich-disabled', this._disabled);
    this.el.classList.toggle('vn2-rich-ro', this._readOnly);
    if(this.editor && !this.destroyed && this.editor.isEditable !== on) this.editor.setEditable(on, false);
    this._state();
  }
  _state(){ try{ if(this.onStateChange) this.onStateChange(); }catch(e){ console.error('❌ rich toolbar paint:', e); } }
  // Put a doc into the field WITHOUT an input event (no autosave). undoable → one history step (Tidy / legacy value
  // writes); otherwise a history-free replace with the history reset (a loaded / remote version).
  setDoc(json, { undoable = false } = {}){
    const doc = json && json.type === 'doc' ? json : emptyDoc();
    this._doc = doc; this._plain = docToPlain(doc);
    if(!this.editor || this.destroyed) return;
    const api = window.HUD_NOTES_RICH;
    this._silent++;
    try{
      if(undoable) api.replaceDocAsOneStep(this.editor, doc);
      else api.applyRemoteDoc(this.editor, doc, this._scroller);
    } finally { this._silent--; }
    this._state();
  }
  // First load of this note's content (content + content_doc). Resolves when the editor is mounted and shows it.
  async load({ content = '', contentDoc = null } = {}){
    const { doc, from } = docForRow(content, contentDoc);
    this.from = from;
    await this._ensureEditor(); // the loaded text becomes the field's value only once the editor shows it (atomic for observers)
    if(this.editor && !this.destroyed) this.setDoc(doc);
    this.loaded = !!this.editor;
    return from;
  }
  async _ensureEditor(){
    if(this.editor || this.destroyed) return;
    this.mountError = null;
    let api;
    try{ api = await loadNotesRich(); }
    catch(e){ this.mountError = e; console.error('❌ notes rich editor load:', e); throw e; }
    if(this.destroyed || this.editor) return;
    this.el.replaceChildren();
    this.editor = api.createNotesEditor({
      element: this.el,
      content: this._doc,
      editable: !this._disabled && !this._readOnly,
      placeholder: this._placeholder,
      foreign: { onTrip: info => (this._onForeignTrip ? this._onForeignTrip(info) : null), canResume: () => !this._disabled && !this._readOnly },
      onUpdate: ({ transaction }) => {
        if(this._silent || !transaction || !transaction.docChanged || transaction.getMeta('hudRemote')) return;
        this._fireInput();
      },
    });
    this._scroller = this.el.closest('.vn2-scroll') || null;
    this.editor.on('transaction', () => this._state());
    const fc = () => { try{ if(this._onFocusChange) this._onFocusChange(); }catch(_){} };
    this.editor.on('focus', fc); this.editor.on('blur', fc);
    this._state();
  }
  attachTo(parent){ if(this.el.parentNode !== parent) parent.appendChild(this.el); this._scroller = this.el.closest('.vn2-scroll') || this._scroller; }
  destroy(){
    if(this.destroyed) return;
    try{ if(this._pinDispose) this._pinDispose(); }catch(_){}   // VV-LISTENER-LEAK: the phone toolbar's viewport listeners go with the engine
    if(this.editor){ try{ this._doc = this.editor.getJSON(); this._plain = docToPlain(this._doc); }catch(_){} try{ this.editor.destroy(); }catch(e){ console.error('❌ rich field destroy:', e); } }
    this.editor = null; this.destroyed = true; this.oninput = null; this._inputL.clear();
  }
}
